import { useMemo, useRef, useState } from "react";
import { useParams } from "@tanstack/react-router";
import {
  Botao, Cartao, CartaoTopo, Selo, Medidor, Anel, Vazio, LinhaDoTempo, ItemDaLinha, AbasDeConteudo,
  Avatar, DicaDeTela, Player, type ControleDoPlayer, type TomDoSelo,
} from "@notes/ui";
import {
  Mic, ExternalLink, ListChecks, MessageSquareWarning, Sparkles, UserRound, ShieldCheck,
  Play, CheckCircle2, Wand2, Quote, Bookmark,
} from "lucide-react";
import { Pagina, Secao, Carregando, Erro } from "../shell/Shell.js";
import { usarReuniao, usarPontePronta, usarReprocessar, abrirNoCrm, mensagemDeErro } from "../lib/api.js";

/** O nome que a extensão mostra no botão é o mesmo que aparece aqui. */
const ROTULO_DA_MARCACAO: Record<string, string> = {
  destaque: "Destaque", nota: "Nota", tarefa: "Tarefa", duvida: "Dúvida",
};
import { dataHora, duracao, marcaDeTempo, ouSemRegistro } from "../lib/formato.js";
import { EstadoDaReuniao } from "./Painel.js";

/**
 * A REUNIÃO. Duas colunas: à esquerda a conversa (transcrição, que é a prova),
 * à direita o que o sistema entendeu dela.
 *
 * A regra que organiza a tela: NADA à direita sem caminho para a evidência à
 * esquerda. Clicar numa evidência do score rola a transcrição até o trecho e o
 * acende. É o que separa avaliação de opinião, e é o que faz o consultor
 * confiar na nota em vez de discutir com ela.
 */
/**
 * POR QUE esta reunião não diz quem falou.
 *
 * A separação de falante deste produto é por CANAL: o microfone é o consultor,
 * a aba é todo mundo do outro lado, e comparar os dois faz do falante um FATO,
 * não palpite de modelo. Faltando uma das faixas não há o que comparar, e a
 * reunião chega inteira — transcrita, resumida, avaliada — com um participante
 * só. Em 09/09/2026 foi exatamente isso, e nada dizia o motivo: parecia defeito
 * da transcrição, quando era o microfone que não tinha entrado.
 *
 * As chaves são a lista fechada de `reuniao.sem_diarizacao`, e o teste confere
 * este mapa contra o CHECK da migração: motivo novo sem frase aqui é motivo que
 * chega à tela como silêncio, que é o defeito que este mapa existe para acabar.
 */
export const PORQUE_SEM_FALANTE: Record<string, string> = {
  sem_microfone: "O microfone não entrou nesta gravação, então não dá para separar quem falou.",
  sem_audio_da_aba: "O áudio da aba não entrou nesta gravação, então não dá para separar quem falou.",
  faixa_falhou: "Uma das faixas de áudio falhou ao ser guardada, então não dá para separar quem falou.",
  nao_separado: "Esta gravação veio num canal só, então não dá para separar quem falou.",
};

/** Nulo quando a reunião TEM separação, ou quando o motivo é desconhecido. */
export function porqueSemFalante(motivo: string | null | undefined): string | null {
  return motivo ? PORQUE_SEM_FALANTE[motivo] ?? null : null;
}

export function Reuniao() {
  const { id } = useParams({ from: "/reunioes/$id" });
  const q = usarReuniao(id);
  const { data: ponte } = usarPontePronta();
  const reprocessar = usarReprocessar();
  const [aba, setAba] = useState("resumo");
  const [destacado, setDestacado] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [tocandoEm, setTocandoEm] = useState<number | null>(null);
  const transcricaoRef = useRef<HTMLOListElement>(null);
  const player = useRef<ControleDoPlayer>(null);

  if (q.isLoading) return <Pagina titulo="Reunião"><Carregando tipo="texto" /></Pagina>;
  if (q.isError) {
    return <Pagina titulo="Reunião"><Erro mensagem={mensagemDeErro(q.error)} aoTentarDeNovo={() => void q.refetch()} /></Pagina>;
  }
  if (!q.data) {
    return (
      <Pagina titulo="Reunião">
        <Vazio titulo="Esta reunião não existe nesta agência"
          descricao="Ou ela foi apagada, ou o endereço é de outra agência. Volte para a lista de reuniões." />
      </Pagina>
    );
  }

  const d = q.data;
  const s = d.processamento.situacao;

  /**
   * Leva a transcrição até o trecho da evidência e o acende.
   * O casamento é pelo tempo de início: é o que a evidência carrega, e é o que
   * o segmento tem. Comparar texto erraria justamente onde o Context Engine
   * corrigiu a palavra.
   */
  const irParaOTrecho = (inicioMs: number) => {
    setDestacado(inicioMs);
    // O áudio vai junto. Antes a evidência levava só o olho até o trecho, e a
    // pessoa tinha que procurar o minuto na gravação à mão para conferir se o
    // sistema entendeu certo.
    player.current?.irPara(inicioMs);
    // A aba NÃO muda: a conversa está à esquerda, sempre visível, e trocar o
    // painel da direita tiraria da tela justamente o que a pessoa estava lendo.
    requestAnimationFrame(() => {
      const alvo = transcricaoRef.current?.querySelector<HTMLElement>(`[data-inicio="${inicioMs}"]`)
        ?? maisProximo(transcricaoRef.current, inicioMs);
      alvo?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const abrirNegociacao = async () => {
    setErro(null);
    try {
      await abrirNoCrm(d.reuniao.negociacaoCrmId
        ? { tipo: "negociacao", id: d.reuniao.negociacaoCrmId }
        : { tipo: "home" });
    } catch (e) { setErro(mensagemDeErro(e)); }
  };

  const participantes = d.participantes ?? [];
  const cliente = participantes.filter((p) => !p.interno);

  const semFalante = porqueSemFalante(d.reuniao.semDiarizacao);

  /**
   * Qual gravação tocar. Telefonia guarda três: o canal do consultor, o do
   * cliente e a mistura dos dois. Quem OUVE quer a mistura; os canais separados
   * existem para a transcrição saber quem falou.
   */
  const gravacoes = d.gravacoes ?? [];
  const paraOuvir = gravacoes.find((g) => g.canal === "mistura")
    ?? gravacoes.find((g) => !g.canal && g.tipo === "audio")
    ?? gravacoes.find((g) => g.tipo === "audio")
    ?? gravacoes[0];

  /** O trecho que está tocando agora: o último que já começou. */
  const emCurso = tocandoEm == null
    ? null
    : d.segmentos.reduce<number | null>(
      (achado, seg) => (seg.inicioMs <= tocandoEm ? seg.inicioMs : achado), null);

  return (
    <Pagina
      largura="larga"
      titulo={d.reuniao.titulo}
      descricao={
        <span className="enotes-reuniao-meta">
          <span>{dataHora(d.reuniao.inicioEm)}</span>
          <span aria-hidden>·</span>
          <span>{duracao(d.reuniao.duracaoS)}</span>
          {participantes.length > 0 && (
            <>
              <span aria-hidden>·</span>
              <span className="enotes-participantes">
                {participantes.slice(0, 4).map((p, i) => (
                  <DicaDeTela key={i} texto={`${p.nome ?? "sem nome"}${p.interno ? " (da agência)" : ""}`}>
                    <span><Avatar nome={p.nome ?? "?"} tamanho={20} /></span>
                  </DicaDeTela>
                ))}
                {participantes.length > 4 && <span className="carimbo">mais {participantes.length - 4}</span>}
              </span>
            </>
          )}
          {semFalante && (
            <>
              <span aria-hidden>·</span>
              <DicaDeTela texto={semFalante}>
                <Selo tom="atencao">sem quem falou</Selo>
              </DicaDeTela>
            </>
          )}
        </span>
      }
      acao={
        <>
          <EstadoDaReuniao status={d.reuniao.status} />
          {ponte?.ativa && (
            <Botao tamanho="sm" variante="contorno" onClick={() => void abrirNegociacao()}>
              <ExternalLink size={14} />
              {d.reuniao.negociacaoCrmId ? "Abrir a negociação" : "Abrir o CRM"}
            </Botao>
          )}
        </>
      }
    >
      {erro && <Secao ordem={0}><Erro mensagem={erro} /></Secao>}

      {s.estado !== "concluida" && (
        <Secao ordem={0}>
          <Cartao className="enotes-chamada" data-tom={s.estado === "falhou" ? "critico" : "atencao"}>
            <span className="enotes-chamada-icone" aria-hidden><Wand2 size={18} /></span>
            <div>
              <p className="enotes-chamada-titulo">
                {s.estado === "rodando" ? `Ainda processando: ${s.feitos} de ${s.total} etapas.` : null}
                {s.estado === "falhou" ? `Algumas etapas falharam: ${s.falhos.join(", ")}.` : null}
                {s.estado === "travada" ? "Processamento parado." : null}
              </p>
              <p className="enotes-chamada-sub">
                {s.estado === "travada" ? s.motivo : "O que já ficou pronto aparece abaixo; o resto entra sozinho."}
              </p>
            </div>
            <Medidor valor={s.feitos} maximo={s.total} rotulo="" tom={s.estado === "falhou" ? "critico" : "marca"} mostrarNumero={false} />
            {(s.estado === "falhou" || s.estado === "travada") && (
              <Botao
                variante="secundario"
                tamanho="sm"
                ocupado={reprocessar.isPending}
                onClick={() => { setErro(null); reprocessar.mutate({ id }, { onError: (e) => setErro(mensagemDeErro(e)) }); }}
              >
                Tentar de novo
              </Botao>
            )}
          </Cartao>
        </Secao>
      )}

      <div className="enotes-reuniao-grade">
        {/* A conversa, que é a prova. */}
        <div className="enotes-reuniao-conversa">
          <Cartao>
            <CartaoTopo
              icone={<Mic size={15} />}
              titulo="A conversa"
              descricao="Trechos marcados foram ajustados pelo vocabulário da agência. O texto original fica guardado."
              acao={<span className="carimbo num">{d.segmentos.length} trechos</span>}
            />
            {paraOuvir && (
              <Player
                ref={player}
                src={`/api/midia/${paraOuvir.id}`}
                duracaoS={paraOuvir.duracaoS}
                aoTempo={setTocandoEm}
                rotulo="a gravação desta conversa"
                video={paraOuvir.tipo === "video"}
              />
            )}
            {d.segmentos.length === 0 ? (
              <Vazio titulo="A transcrição ainda não está pronta"
                descricao="Assim que o áudio for transcrito, cada fala aparece aqui com a marca de tempo." />
            ) : (
              <ol className="enotes-transcricao" ref={transcricaoRef}>
                {d.segmentos.map((seg) => (
                  <li
                    key={seg.id}
                    data-inicio={seg.inicioMs}
                    className="enotes-fala"
                    data-corrigido={seg.corrigido || undefined}
                    data-destacado={destacado === seg.inicioMs || undefined}
                    data-tocando={emCurso === seg.inicioMs || undefined}
                  >
                    {/* O tempo vira botão quando há o que ouvir: ler um trecho e
                        querer escutá-lo é o mesmo movimento. */}
                    {paraOuvir ? (
                      <button
                        type="button"
                        className="carimbo enotes-fala-tempo enotes-fala-ir"
                        onClick={() => player.current?.irPara(seg.inicioMs)}
                        aria-label={`Ouvir a partir de ${marcaDeTempo(seg.inicioMs)}`}
                      >
                        {marcaDeTempo(seg.inicioMs)}
                      </button>
                    ) : (
                      <span className="carimbo enotes-fala-tempo">{marcaDeTempo(seg.inicioMs)}</span>
                    )}
                    <span className="enotes-fala-quem">{ouSemRegistro(seg.falante)}</span>
                    <span className="enotes-fala-texto">{seg.texto}</span>
                  </li>
                ))}
              </ol>
            )}
          </Cartao>
        </div>

        {/* O que o sistema entendeu. */}
        <div className="enotes-reuniao-lado">
          <AbasDeConteudo
            valor={aba}
            aoTrocar={setAba}
            abas={[
              {
                chave: "resumo", rotulo: "Resumo", icone: <Sparkles size={14} />,
                conteudo: (
                  <div className="enotes-pilha">
                    <Cartao>
                      <CartaoTopo titulo="O que aconteceu" />
                      <p className="enotes-resumo">{ouSemRegistro(d.resumo)}</p>
                    </Cartao>

                    <Cartao>
                      <CartaoTopo icone={<ListChecks size={15} />} titulo="Combinados"
                        descricao="Fecham sozinhos quando a prova chega do CRM." />
                      {d.compromissos.length === 0 ? (
                        <Vazio titulo="Nada foi prometido nesta conversa" />
                      ) : (
                        <LinhaDoTempo>
                          {d.compromissos.map((c) => (
                            <ItemDaLinha
                              key={c.id}
                              tom={c.cumpridoEm ? "bom" : "atencao"}
                              icone={c.cumpridoEm ? <CheckCircle2 size={15} /> : <ListChecks size={15} />}
                              titulo={<><strong>{c.quem === "consultor" ? "Você" : "O cliente"}:</strong> {c.descricao}</>}
                              quando={c.prazo ?? "sem prazo"}
                            />
                          ))}
                        </LinhaDoTempo>
                      )}
                    </Cartao>

                    {d.tarefas.length > 0 && (
                      <Cartao>
                        <CartaoTopo titulo="Tarefas que saíram daqui" />
                        <LinhaDoTempo>
                          {d.tarefas.map((t) => (
                            <ItemDaLinha key={t.id} tom="marca" icone={<ListChecks size={15} />}
                              titulo={t.titulo} quando={t.prazo ?? "sem prazo"} />
                          ))}
                        </LinhaDoTempo>
                      </Cartao>
                    )}

                    {d.marcacoes.length > 0 && (
                      <Cartao>
                        <CartaoTopo icone={<Bookmark size={15} />} titulo="Momentos marcados na gravação"
                          descricao="O que você marcou pela extensão enquanto a conversa acontecia. Clique para ouvir." />
                        <LinhaDoTempo>
                          {d.marcacoes.map((m, i) => (
                            <ItemDaLinha key={i} tom={m.tipo === "duvida" ? "atencao" : m.tipo === "tarefa" ? "marca" : "neutro"}
                              icone={<Bookmark size={15} />}
                              titulo={<><span className="enotes-tema">{ROTULO_DA_MARCACAO[m.tipo] ?? m.tipo}</span> {m.texto ?? ""}</>}
                              quando={marcaDeTempo(m.emMs)}
                              acao={<Botao tamanho="sm" variante="fantasma" onClick={() => irParaOTrecho(m.emMs)}><Play size={13} /> Ouvir este momento</Botao>} />
                          ))}
                        </LinhaDoTempo>
                      </Cartao>
                    )}

                    {d.objecoes.length > 0 && (
                      <Cartao>
                        <CartaoTopo icone={<MessageSquareWarning size={15} />} titulo="Objeções do cliente" />
                        <LinhaDoTempo>
                          {d.objecoes.map((o, i) => (
                            <ItemDaLinha key={i} tom={o.tratada ? "bom" : "critico"}
                              icone={<MessageSquareWarning size={15} />}
                              titulo={<><span className="enotes-tema">{o.tema}</span> {ouSemRegistro(o.descricao)}</>}
                              quando={o.tratada ? "tratada" : "em aberto"} />
                          ))}
                        </LinhaDoTempo>
                      </Cartao>
                    )}
                  </div>
                ),
              },
              {
                chave: "viajante", rotulo: "Viajante", icone: <UserRound size={14} />,
                conteudo: (
                  <Cartao>
                    <CartaoTopo icone={<UserRound size={15} />} titulo="Quem estava na conversa"
                      descricao="Quem falou, quanto falou, e de que lado da mesa." />
                    {participantes.length === 0 ? (
                      <Vazio titulo="Sem participantes registrados"
                        descricao="Quando a captura identificar as vozes, elas aparecem aqui." />
                    ) : (
                      <ul className="enotes-participantes-lista">
                        {participantes.map((p, i) => {
                          const totalFala = participantes.reduce((t, x) => t + (x.falaMs ?? 0), 0);
                          const pct = totalFala > 0 ? ((p.falaMs ?? 0) / totalFala) * 100 : 0;
                          return (
                            <li key={i} className="enotes-participante">
                              <Avatar nome={p.nome ?? "?"} tamanho={30} />
                              <div className="enotes-participante-texto">
                                <span className="enotes-participante-nome">{ouSemRegistro(p.nome)}</span>
                                <span className="carimbo">{p.interno ? "da agência" : "cliente"} · {p.papel}</span>
                              </div>
                              <Medidor valor={pct} rotulo="" tom={p.interno ? "marca" : "info"} mostrarNumero={false} />
                              <span className="num enotes-participante-pct">{Math.round(pct)}%</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {cliente.length > 0 && d.reuniao.contatoCrmId && (
                      <p className="enotes-cartao-sub" style={{ marginTop: "0.9rem" }}>
                        Contato no CRM: <span className="num">{d.reuniao.contatoCrmId}</span>
                      </p>
                    )}
                  </Cartao>
                ),
              },
              {
                chave: "score", rotulo: "Nota", icone: <Sparkles size={14} />,
                conteudo: d.score ? (
                  <Cartao>
                    <CartaoTopo
                      titulo="Entur Score"
                      descricao="Cada dimensão vem com o trecho que a sustenta. Clique no trecho para ouvir onde foi dito."
                      acao={<Anel valor={d.score.total} legenda={rotuloDaFaixa(d.score.faixa)} />}
                    />
                    <ul className="enotes-dimensoes">
                      {d.score.dimensoes.map((dim) => {
                        const evidencias = Array.isArray(dim.evidencia)
                          ? (dim.evidencia as Array<{ inicioMs: number; trecho: string }>)
                          : [];
                        const tom: TomDoSelo = dim.nota >= 8 ? "bom" : dim.nota >= 6 ? "atencao" : "critico";
                        return (
                          <li key={dim.chave} className="enotes-dimensao">
                            <Medidor valor={dim.nota * 10} rotulo={dim.rotulo} tom={tom} mostrarNumero={false} />
                            <span className="enotes-dimensao-nota num">{dim.nota} de 10</span>
                            {evidencias.map((e, i) => (
                              <button key={i} className="enotes-evidencia" onClick={() => irParaOTrecho(e.inicioMs)}
                                title="Ver este trecho na conversa">
                                <Quote size={12} aria-hidden />
                                <span className="carimbo">{marcaDeTempo(e.inicioMs)}</span>
                                <span className="enotes-evidencia-texto">{e.trecho}</span>
                                <Play size={11} aria-hidden className="enotes-evidencia-play" />
                              </button>
                            ))}
                            {dim.sugestao && <p className="enotes-sugestao">{dim.sugestao}</p>}
                          </li>
                        );
                      })}
                    </ul>
                  </Cartao>
                ) : (
                  <Vazio titulo="Esta conversa ainda não foi avaliada"
                    descricao="A nota entra quando o processamento chegar na etapa de avaliação." />
                ),
              },
              {
                chave: "coach", rotulo: "Coach", icone: <Sparkles size={14} />,
                conteudo: d.coach ? (
                  <div className="enotes-pilha">
                    <Cartao>
                      <CartaoTopo titulo="O que repetir" icone={<CheckCircle2 size={15} />} />
                      <ul className="enotes-bullets">{d.coach.elogios.map((e, i) => <li key={i}>{e}</li>)}</ul>
                    </Cartao>
                    <Cartao>
                      <CartaoTopo titulo="O que ajustar" icone={<Wand2 size={15} />} />
                      <ul className="enotes-bullets">{d.coach.melhorias.map((e, i) => <li key={i}>{e}</li>)}</ul>
                    </Cartao>
                    {d.coach.proximoPasso && (
                      <Cartao className="enotes-chamada">
                        <span className="enotes-chamada-icone" aria-hidden><ListChecks size={18} /></span>
                        <div>
                          <p className="enotes-chamada-titulo">Próximo passo sugerido</p>
                          <p className="enotes-chamada-sub">{d.coach.proximoPasso}</p>
                        </div>
                      </Cartao>
                    )}
                  </div>
                ) : (
                  <Vazio titulo="Sem coaching nesta conversa"
                    descricao="O coach entra depois da avaliação, e sempre com elogio junto da correção." />
                ),
              },
              {
                chave: "acoes", rotulo: "Ações", icone: <ShieldCheck size={14} />,
                contador: d.acoes.filter((a) => a.status === "proposta").length,
                conteudo: d.acoes.length === 0 ? (
                  <Vazio titulo="Nenhuma ação saiu desta conversa"
                    descricao="Quando a conversa gerar algo para fazer no CRM, aparece aqui, e nada acontece antes de alguém aprovar." />
                ) : (
                  <Cartao>
                    <LinhaDoTempo>
                      {d.acoes.map((a) => (
                        <ItemDaLinha
                          key={a.id}
                          tom={a.status === "executada" ? "bom" : a.status === "rejeitada" || a.status === "falhou" ? "critico" : "atencao"}
                          icone={<ShieldCheck size={15} />}
                          titulo={rotuloDaAcao(a.tipo)}
                          quando={rotuloDoEstadoDaAcao(a.status)}
                        >
                          {a.justificativa && <p className="enotes-lt-nota">{a.justificativa}</p>}
                        </ItemDaLinha>
                      ))}
                    </LinhaDoTempo>
                  </Cartao>
                ),
              },
            ]}
          />
        </div>
      </div>
    </Pagina>
  );
}

/** O trecho mais próximo do tempo pedido, quando não há casamento exato. */
function maisProximo(lista: HTMLOListElement | null, inicioMs: number): HTMLElement | null {
  if (!lista) return null;
  let melhor: HTMLElement | null = null;
  let menorDistancia = Number.POSITIVE_INFINITY;
  for (const el of Array.from(lista.querySelectorAll<HTMLElement>("[data-inicio]"))) {
    const v = Number(el.dataset["inicio"]);
    const dist = Math.abs(v - inicioMs);
    if (dist < menorDistancia) { menorDistancia = dist; melhor = el; }
  }
  return melhor;
}

const rotuloDaFaixa = (f: string): string =>
  ({ excelente: "excelente", bom: "bom", atencao: "atenção", critico: "crítico" }[f] ?? f);

export const rotuloDaAcao = (tipo: string): string => ({
  "crm.nota.criar": "Registrar o resumo na negociação",
  "crm.tarefa.criar": "Criar tarefa no CRM",
  "crm.negociacao.atualizar_campos": "Atualizar campos da negociação",
  "crm.contato.preferencias": "Guardar as preferências do viajante",
  "crm.campo_personalizado.definir": "Preencher campo personalizado",
  "agenda.evento.criar": "Marcar compromisso na agenda",
  "agenda.evento.mover": "Remarcar compromisso",
  "agenda.evento.cancelar": "Cancelar compromisso",
  "notes.lembrete.criar": "Criar lembrete interno",
  "notes.followup.rascunho": "Preparar rascunho de retorno",
  "whatsapp.enviar": "Enviar mensagem ao cliente",
  "documento.anexar": "Anexar documento",
  "crm.negociacao.marcar_perdida": "Marcar a negociação como perdida",
}[tipo] ?? tipo);

export const rotuloDoEstadoDaAcao = (s: string): string => ({
  proposta: "esperando você", aprovada: "aprovada", rejeitada: "recusada",
  executada: "feita no CRM", despachada: "a caminho do CRM", falhou: "não deu certo", expirada: "expirou",
}[s] ?? s);
